import { put, select, takeLatest, takeEvery, fork } from 'redux-saga/effects';
import { workspace as Api, historyFeed as hfApi, contracting as coApi } from '../../api';
import { resetChannels, resetConversations/* ,selectContract, selectChannel*/ } from './store/actions';
import { push as routerPush } from 'react-router-redux';
import { updateProps } from '../messaging/messagingStore/messaging-actions';
import * as wkspConst from './store/constants';
import * as inboxConst from '../messaging/messagingStore/messaging-constants';
import R from 'ramda';
import { apiSaga } from '../../sagas';

const getRoute = state => state.routing.locationBeforeTransitions.pathname;
const getConnection = state => state.workspace.selectedConnection;
const getChannel = state => state.workspace.selectedChannel;
const getContract = state => state.workspace.selectedContract;
const getChannels = state => state.workspace.channels;
const getConversations = state => state.workspace.conversations;
const routePath = '/workspace';

function* fetchConnections() {
  const selectedConnection = yield select(getConnection);
  yield* apiSaga(wkspConst.CONNECTIONS_FETCH, Api.getConnections, [selectedConnection]);
}
function* deleteMessage({ payload, type }) {
  yield put({ type: wkspConst.MESSAGE_DISABLE, payload });
  yield* apiSaga(type, Api.deleteMessage, [payload]);
}

function* addFilesToContract({ payload }) {
  const contractId = yield select(getContract);
  const files = (yield select(getConversations)).filter(e => e.key === payload)[0].attachments;
  yield put({ type: wkspConst.MESSAGE_DISABLE, payload });
  yield* apiSaga(wkspConst.MESSAGE_ADD_FILES_TO_CONTRACT, coApi.addFilesToContract, [{ contractId, files, msgId: payload }]);
}

function* fetchChannels() {
  const connection = yield select(getConnection);
  const channel = yield select(getChannel);
  const contract = yield select(getContract);
  if (connection) yield* apiSaga(wkspConst.CHANNELS_FETCH, Api.getChannels, [connection, channel, contract]);
}

function* fetchContractConversations() {
  const route = yield select(getRoute);
  if (route.substring(0, routePath.length) !== routePath) {
    // component is in contacting area
    const getContractFromContracting = state => state.contracting.contractActivitiesProducts.getIn(['leftNav', 'contractId']);
    const selectedContract = yield select(getContractFromContracting);
    yield put({ type: 'LOADING_HISTORY_FEEDS' });
    yield* apiSaga('LOADING_HISTORY_FEEDS', hfApi.getHistory, [selectedContract]);
    return;
  }
  // component is inside the workspace
  const connection = yield select(getConnection);
  const selectedContract = yield select(getContract);
  yield* apiSaga(wkspConst.CONVERSATIONS_FETCH, Api.getConversations, [connection, selectedContract]);
}

function* fetchConversations() {
  const connection = yield select(getConnection);
  const channel = yield select(getChannel);
  const contract = yield select(getContract);
  if (connection && channel) {
    if (contract && channel === 'contracting') {
      yield* fetchContractConversations();
    } else {
      yield* apiSaga(wkspConst.CONVERSATIONS_FETCH, Api.getConversations, [connection, channel]);
    }
  }
}

function* saveSelectedChannel() {
  const selectedConnection = yield select(getConnection);
  const selectedChannel = yield select(getChannel);
  if (selectedConnection && selectedChannel) {
    yield* apiSaga(wkspConst.CHANNELS_SELECT_SAVE, Api.saveSelectedChannel, [
      selectedConnection,
      selectedChannel,
    ]);
  }
}

function* archiveConnection(action) {
  const selectedConnection = yield select(getConnection);
  if (selectedConnection === action.payload) yield put(routerPush('/workspace'));
  yield* apiSaga(wkspConst.CONNECTIONS_ARCHIVE, Api.archiveConnection, [action.payload]);
}

function* archiveConversation(action) {
  const connection = yield select(getConnection);
  const channel = yield select(getChannel);
  const contract = yield select(getContract);
  if (channel === action.payload.channel && contract === action.payload.contract) {
    if (connection) {
      yield put(routerPush(`/workspace/${connection}/contracting`));
    } else {
      yield put(routerPush(`/workspace/${connection}/general`));
    }
  }
  yield* apiSaga(wkspConst.CHANNELS_ARCHIVE, Api.archiveConversation, [connection, action.payload.channel, action.payload.contract]);
}

function* loadLastSelectedChannel() { // open the last selected channel, server side
  const getSelectedConnection = state => state.workspace.selectedConnection;
  const selectedConnection = yield select(getSelectedConnection);

  const getConnections = state => state.workspace.connections;
  const selectedConnectionData = (yield select(getConnections)).filter(e => (e.id === (selectedConnection)))[0];

  const getSelectedChannel = state => state.workspace.selectedChannel;
  const selectedChannel = yield select(getSelectedChannel);

  if (!selectedChannel && selectedConnectionData && selectedConnectionData.lastChannel) {
    if (selectedConnectionData.lastContract) {
      yield put(routerPush(`/workspace/${selectedConnection}/${selectedConnectionData.lastChannel}/${selectedConnectionData.lastContract}`));
    } else {
      yield put(routerPush(`/workspace/${selectedConnection}/${selectedConnectionData.lastChannel}`));
    }
  }
}

function* createMessage(action) {
  const selectedConnection = yield select(getConnection);
  const selectedChannel = yield select(getChannel);
  const convos = yield select(getConversations);
  yield* apiSaga(wkspConst.MESSAGE_CREATE, Api.newMessage, [{
    type: selectedChannel === 'notes' ? 'note' : 'message',
    contractHFIds: selectedChannel && selectedChannel !== 'general' ? [selectedChannel] : [],
    partnerIds: selectedConnection ? [selectedConnection] : [],
    mostRecentIncomingMsg: R.call(R.compose(
      R.head,
      R.sortBy(R.descend(R.prop('timestamp'))),
      R.filter(obj => !obj.isSender),
    ), convos),
    ...action.payload,
  }]);
}

function* markConversationRead({ payload }) {
  if (payload) {
    const { assetType, assetId, messageId } = payload;
    yield* apiSaga(
      wkspConst.CONVERSATIONS_READ,
      Api.conversationsRead,
      [
        {
          assetType,
          assetId,
          messageId,
        },
      ]
    );
    return;
  }
  const route = yield select(getRoute);
  if (route.substring(0, routePath.length) !== routePath) {
    // component is in contacting area
    const getLeftNav = state => state.contracting.contractActivitiesProducts.get('leftNav').toJS();
    const { historyFeedId } = yield select(getLeftNav);
    if (!historyFeedId) return; // no contract randomly called
    yield* apiSaga(
      wkspConst.CONVERSATIONS_READ,
      Api.conversationsRead,
      [{ assetId: historyFeedId, assetType: 'contract' }],
    );
    return;
  }
  // component is in workspace
  const selectedConnection = yield select(getConnection);
  const selectedChannel = yield select(getChannel);
  if (!(selectedConnection && selectedChannel)) return;
  yield* apiSaga(
    wkspConst.CONVERSATIONS_READ,
    Api.conversationsRead,
    [{
      assetId: selectedConnection,
      assetType: 'partner',
      // Commented this because right now the frontend doesn't handle channels other than contracting
      // history feed "channel" which is taken care of by the "if (payload)" block above
      // triggered by markReadWithParams, which is called in comments.jsx
      // channelId: selectedChannel,
    }],
  );
}

function* refreshStatusMessages() {
  yield* apiSaga(wkspConst.STATUS_MESSAGES, Api.statusMessages, []);
}

// function* clearSelectedContract() {
//   yield put(selectContract(null));
// }
//
// function* clearSelectedChannel() {
//   yield put(selectChannel(null));
// }

// TODO: This function should be made global for frontend error handling
const logError = (payload) => {
  console.error(payload);
};

function* fetchChanges({ payload }) {
  if (payload.before.connection !== payload.connection) {
    yield put(resetChannels());
    yield fork(fetchChannels);
  } else {
    const channels = yield select(getChannels);
    if (channels.filter(e => (e.channelId === payload.channel)).length === 0) {
      // channel not found, it was created with the link
      yield fork(fetchChannels);
    }
  }
  if (payload.before.connection !== payload.connection ||
    payload.before.channel !== payload.channel) {
    yield put(resetConversations());
    yield fork(fetchConversations);
    yield fork(saveSelectedChannel);
  }
}

function* contractConversationsRefresh(action) {
  yield put({ type: 'GET_HISTORY_FEEDS', payload: { response: action.payload } });
  yield* markConversationRead({});
}

function* inboxCountLeft(action) {
  const selectedChannel = yield select(getChannel);
  // don't clear if the selected conversation is not the same.
  if (selectedChannel !== action.payload.messageId) return;
  // when the count of unread is 0 go to workspace homepage, unassinged should be hidden.
  yield put(updateProps('convs', { selectedConv: {}, messages: [] }));
  yield put({ type: inboxConst.MSGS_LOADED_INITIALLY, payload: {
    conv: {},
    messages: [],
    lastMsgId: '',
    messagesLoaded: true,
    loadingMessages: false,
  } });
  yield put(routerPush('/workspace/inbox'));
}

function* markAcceptedRead({ payload }) {
  yield* apiSaga(wkspConst.STATUS_CONNECTIONS_MARK_READ, Api.unreadAcceptedRead, [payload]);
}

export const workspaceSagas = [
  takeLatest(wkspConst.CONNECTIONS_FETCH, fetchConnections),
  takeLatest(wkspConst.CHANNELS_FETCH, fetchChannels),
  takeLatest(wkspConst.CHANGE_SELECTION, fetchChanges),
  takeLatest(wkspConst.CONVERSATIONS_FETCH, fetchConversations),
  takeLatest(wkspConst.CONNECTIONS_ARCHIVE, archiveConnection),
  takeLatest(`${wkspConst.CONNECTIONS_ARCHIVE}_SUCCESS`, fetchConnections),
  takeLatest(wkspConst.CHANNELS_ARCHIVE, archiveConversation),
  takeLatest(`${wkspConst.CHANNELS_ARCHIVE}_SUCCESS`, fetchChannels),
  takeLatest(`${wkspConst.CHANNELS_FETCH}_SUCCESS`, loadLastSelectedChannel),
  takeLatest(`${wkspConst.CONVERSATIONS_READ}`, markConversationRead),
  takeLatest(wkspConst.CONVERSATIONS_READ_WITH_PARAMS, markConversationRead),
  takeLatest(wkspConst.MESSAGE_CREATE, createMessage),
  takeLatest(`${wkspConst.MESSAGE_CREATE}_SUCCESS`, fetchConversations), // refresh the conversation list
  takeLatest(wkspConst.STATUS_MESSAGES, refreshStatusMessages),
  takeEvery(`${wkspConst.CONNECTIONS_FETCH}_ERROR`, logError),
  takeEvery(`${wkspConst.CONVERSATIONS_FETCH}_ERROR`, logError),
  takeLatest('SUBMIT_CONTRACTING_COMMENT_SUCCESS', fetchContractConversations),
  takeLatest('LOADING_HISTORY_FEEDS_SUCCESS', contractConversationsRefresh),
  takeEvery(wkspConst.MESSAGE_DELETE, deleteMessage),
  takeLatest(`${wkspConst.MESSAGE_DELETE}_SUCCESS`, fetchConversations),
  takeLatest(inboxConst.TOGGLE_CONV_DONESS_SUCCESS, inboxCountLeft),
  takeEvery(wkspConst.MESSAGE_ADD_FILES_TO_CONTRACT, addFilesToContract),
  takeEvery(`${wkspConst.MESSAGE_ADD_FILES_TO_CONTRACT}_SUCCESS`, fetchConversations),
  takeEvery(wkspConst.STATUS_CONNECTIONS_MARK_READ, markAcceptedRead),
];



// WEBPACK FOOTER //
// ./src/app/components/workspace/sagas.js